import { mapGetters, mapActions, mapMutations } from 'vuex'

import MarkdownEditor from '@/components/MarkdownEditor/MarkdownEditor.vue'
import SnackbarAction from '@/components/SnackbarAction/SnackbarAction.vue'
import ChatGPT from '@/components/ChatGPT/ChatGPT.vue'
import { statuses, funnel, types } from '@/utils/clusterVariables.js'

export default {
  name: 'TranslatedArticles',
  data () {
    return {
      isLoading: {
        articles: false,
        setStatus: null,
        setStatusPublished: false,
        setStatusReviewed: false,
        upload: false,
        uploadAll: false,
        reject: null,
        savingFinalDraft: false,
        rewrite: false,
        translation: false,
        originalArticle: false
      },

      // Enhanced autosave configuration
      autosave: {
        timeout: null,
        delay: 1000,
        retryCount: 0,
        maxRetries: 3,
        retryDelay: 2000,
        lastSavedContent: '',
        saveInProgress: false,
        hasError: false,
        pendingSave: false
      },

      tableHeaders: [
        { text: '', value: 'select', sortable: false },
        { text: 'Title', value: 'final' },
        { text: 'Slug', value: 'slug' },
        { text: 'Language', value: 'lang' },
        { text: 'Status', value: 'status' },
        { text: 'Last updated', value: 'last_updated' },
        { text: '', value: 'action' }
      ],
      uploadProgressHeaders: [
        { text: 'Title', value: 'title', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Result', value: 'result', sortable: false },
        { text: 'Error reason', value: 'errorReason', sortable: false }
      ],
      tabs: [
        { text: 'Unpublished', value: 'unpublished' },
        { text: 'Original', value: 'original' }
      ],
      activeTab: 0,
      currentLanguage: 'unpublished',

      selectedArticle: null,
      selectedArticleContent: null,
      originalArticle: null,
      originalArticleContent: '',
      uploadProgressDialog: {
        open: false,
        title: 'Uploading translated articles',
        items: []
      },
      statuses,
      funnel,
      types,
      selectedArticles: [],
      showSnackbar: false,
      showCopilot: false,
      translationLoadLimit: 30,
      translationUploadLimit: 25,
      translationUploadDelay: 1200
    }
  },
  watch: {
    async $route () {
      await this.loadData()
    },
    async activeTab () {
      this.currentLanguage = this.tabs[this.activeTab]?.value || 'original'
      this.clearSelectedArticles()
      this.updateTableHeaders()
      await this.loadData()
    },
    selectedArticle: {
      handler (newVal, oldVal) {
        if (oldVal && this.getArticleSelectionKey(newVal) !== this.getArticleSelectionKey(oldVal)) {
          // Clear any pending saves when switching articles
          this.clearAutosave()
        }

        if (newVal) {
          window.addEventListener('keydown', this.handleEscKey)
          // Initialize autosave state for new article
          this.autosave.lastSavedContent = newVal.final || ''
          this.autosave.hasError = false
          this.autosave.retryCount = 0
        } else {
          window.removeEventListener('keydown', this.handleEscKey)
          this.clearAutosave()
        }
      }
    },
    selectedArticleContent: {
      handler (newValue) {
        if (newValue !== undefined && newValue !== this.autosave.lastSavedContent) {
          this.scheduleAutosave()
        }
      },
      immediate: false
    },
    selectedArticles: {
      handler (newVal) {
        console.log('Selected articles changed:', newVal.length)
        this.showSnackbar = newVal.length > 0
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters([
      'topics',
      'translatedArticles',
      'availableLanguages',
      'activeContentplan',
      'activeWorkspace'
    ]),
    articles () {
      if (this.currentLanguage === 'unpublished') return this.filterUnpublishedArticles(this.translatedArticles)
      return this.translatedArticles
    },
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    contentplanName () {
      return this.activeContentplan ? this.activeContentplan.name : ''
    },
    remainingCredits () {
      return this.activeWorkspace?.remaining_article_credits
    },
    hasSelectedArticles () {
      return this.showSnackbar
    },
    snackbarActions () {
      const actions = [
        {
          text: 'Copy Selected',
          event: 'copy-selected',
          loading: false,
          disabled: false
        }
      ]

      if (this.currentLanguage !== 'original') {
        actions.unshift({
          text: 'Upload',
          event: 'upload-selected',
          loading: this.isLoading.upload,
          disabled: this.isUploadSelectedDisabled
        })
      }

      return actions
    },
    snackbarTitle () {
      if (!this.selectedArticles?.length) return ''
      return this.selectedArticles.length + ' article(s) selected'
    },
    // Enhanced computed properties for autosave
    isSaving () {
      return this.autosave.saveInProgress
    },
    hasSaveError () {
      return this.autosave.hasError
    },
    isUploadDisabled () {
      const integration = this.activeContentplan?.main_cms_integration
      if (integration === 'none') return true
      if (!['wordpress', 'webflow', 'magento', 'shopify', 'custom-cms'].includes(integration)) return true
      return !this.currentLanguage || this.currentLanguage === 'original'
    },
    isUploadAllDisabled () {
      return this.isUploadDisabled || this.isLoading.upload || this.isLoading.uploadAll
    },
    uploadTargetCount () {
      return this.getUploadTargets().length
    },
    isUploadSelectedDisabled () {
      return this.isUploadDisabled || this.isLoading.upload || this.isLoading.uploadAll || this.uploadTargetCount === 0
    },
    uploadDisabledMessage () {
      const integration = this.activeContentplan?.main_cms_integration
      if (integration === 'none') {
        return 'Please set up a CMS integration in Contentplan Settings before uploading articles'
      }
      if (!['wordpress', 'webflow', 'magento', 'shopify', 'custom-cms'].includes(integration)) {
        return 'Translated uploads are currently supported only for WordPress, Webflow, Magento, Shopify, and Custom CMS integrations'
      }
      return 'Select a translation language to upload.'
    },
    selectedArticleLanguage () {
      return this.getSelectedArticleLanguage(this.selectedArticle)
    },
    isSelectedArticleTranslated () {
      return Boolean(this.selectedArticleLanguage && this.selectedArticleLanguage !== 'original')
    },
    isSelectedArticleUploadDisabled () {
      return !this.isSelectedArticleTranslated || this.isUploadDisabled || this.isLoading.upload || this.isLoading.uploadAll
    },
    isUploadProgressRunning () {
      return this.isLoading.upload || this.isLoading.uploadAll || this.uploadProgressDialog.items.some(item => ['queued', 'uploading'].includes(item.status))
    },
    uploadProgressSummary () {
      const total = this.uploadProgressDialog.items.length
      if (!total) return 'Preparing upload queue...'

      const successCount = this.uploadProgressDialog.items.filter(item => item.result === 'success').length
      const errorCount = this.uploadProgressDialog.items.filter(item => item.result === 'error').length
      const queuedCount = this.uploadProgressDialog.items.filter(item => item.status === 'queued').length
      const uploadingCount = this.uploadProgressDialog.items.filter(item => item.status === 'uploading').length

      return `${successCount} success, ${errorCount} error, ${uploadingCount} uploading, ${queuedCount} queued`
    }
  },
  methods: {
    ...mapActions([
      'articleTranslationsGet',
      'articleTranslationsPut',
      'sendWebsocketMessage',
      'uploadTranslatedArticle'
    ]),
    ...mapMutations([
      'addProgressTracker',
      'setTranslatedArticles',
      'setSnackbar'
    ]),

    // Enhanced autosave methods
    scheduleAutosave () {
      if (this.autosave.saveInProgress) {
        this.autosave.pendingSave = true
        return
      }

      this.clearAutosaveTimeout()
      this.autosave.timeout = setTimeout(() => {
        this.performAutosave()
      }, this.autosave.delay)
    },

    clearAutosave () {
      this.clearAutosaveTimeout()
      this.autosave.saveInProgress = false
      this.autosave.pendingSave = false
      this.autosave.hasError = false
      this.autosave.retryCount = 0
    },

    clearAutosaveTimeout () {
      if (this.autosave.timeout) {
        clearTimeout(this.autosave.timeout)
        this.autosave.timeout = null
      }
    },

    async performAutosave () {
      if (!this.selectedArticle?.article_id || this.autosave.saveInProgress) return

      const contentToSave = this.selectedArticleContent
      if (contentToSave === this.autosave.lastSavedContent) return
      const language = this.getSaveLanguage(this.selectedArticle)
      if (!language) return

      this.autosave.saveInProgress = true
      this.autosave.hasError = false
      this.isLoading.savingFinalDraft = true

      try {
        await this.articleTranslationsPut({
          contentplanId: this.contentplanId,
          articleId: this.selectedArticle.article_id,
          language,
          final: contentToSave
        })

        // Update local state on successful save
        this.autosave.lastSavedContent = contentToSave
        if (this.selectedArticle) {
          this.selectedArticle.final = contentToSave
        }
        this.autosave.retryCount = 0

        console.log('Content saved successfully')
      } catch (error) {
        console.error('Failed to save content:', error)
        this.autosave.hasError = true

        // Retry logic
        if (this.autosave.retryCount < this.autosave.maxRetries) {
          this.autosave.retryCount++
          console.log(`Retrying save (attempt ${this.autosave.retryCount}/${this.autosave.maxRetries})`)

          setTimeout(() => {
            this.performAutosave()
          }, this.autosave.retryDelay * this.autosave.retryCount)
        } else {
          this.setSnackbar({
            open: true,
            text: 'Failed to save changes after multiple attempts. Please try again later.',
            type: 'error'
          })
        }
      } finally {
        this.autosave.saveInProgress = false
        this.isLoading.savingFinalDraft = false

        // Handle pending save if there was one
        if (this.autosave.pendingSave) {
          this.autosave.pendingSave = false
          this.scheduleAutosave()
        }
      }
    },

    async loadData () {
      this.isLoading.articles = true
      document.title = 'Translated articles | ContentGecko'

      try {
        const language = this.getFetchLanguage()
        const status = this.getFetchStatus()
        const response = await this.loadArticleTranslationChunks({
          contentplanId: this.contentplanId,
          language,
          status
        })
        this.setTranslatedArticles(response)

        // Initialize tabs based on available languages
        this.initializeTabs()
      } catch (error) {
        console.error('Error loading data:', error)
      }

      this.isLoading.articles = false
    },

    initializeTabs () {
      const newTabs = [
        { text: 'Unpublished', value: 'unpublished' },
        { text: 'Original', value: 'original' }
      ]

      if (this.availableLanguages && this.availableLanguages.length > 0) {
        this.availableLanguages.forEach(lang => {
          newTabs.push({ text: lang.toUpperCase(), value: lang })
        })
      }

      this.tabs = newTabs
      if (!this.tabs[this.activeTab] || this.tabs[this.activeTab].value !== this.currentLanguage) {
        const tabIndex = this.tabs.findIndex(tab => tab.value === this.currentLanguage)
        this.activeTab = tabIndex === -1 ? 0 : tabIndex
        this.currentLanguage = this.tabs[this.activeTab]?.value || 'unpublished'
      }
    },
    updateTableHeaders () {
      const newHeaders = [
        { text: '', value: 'select', sortable: false },
        { text: 'Title', value: 'final' },
        { text: 'Slug', value: 'slug' }
      ]

      if (this.currentLanguage === 'unpublished') newHeaders.push({ text: 'Language', value: 'lang' })

      newHeaders.push(
        { text: 'Status', value: 'status' },
        { text: 'Last updated', value: 'last_updated' },
        { text: '', value: 'action' }
      )

      this.tableHeaders = newHeaders
    },
    getFetchLanguage () {
      if (this.currentLanguage === 'unpublished') return undefined
      return this.currentLanguage
    },
    getFetchStatus () {
      if (this.currentLanguage === 'unpublished') return 'translated'
      return undefined
    },
    async loadArticleTranslationChunks ({ contentplanId, language, status }) {
      let offset = 0
      let availableLanguages = []
      let articles = {}
      let hasMore = true

      while (hasMore) {
        const response = await this.articleTranslationsGet({
          contentplanId,
          language,
          status,
          limit: this.translationLoadLimit,
          offset,
          straightReturn: true
        })

        articles = this.mergeGroupedArticles(articles, response?.articles)
        availableLanguages = response?.availableLanguages || availableLanguages
        hasMore = Boolean(response?.meta?.hasMore)
        offset = response?.meta?.nextOffset || offset + this.translationLoadLimit
      }

      return { articles, availableLanguages }
    },
    mergeGroupedArticles (currentArticles, nextArticles) {
      const mergedArticles = { ...currentArticles }
      Object.entries(nextArticles || {}).forEach(([topicName, articles]) => {
        if (!mergedArticles[topicName]) mergedArticles[topicName] = []
        mergedArticles[topicName].push(...articles)
      })
      return mergedArticles
    },
    getSaveLanguage (article) {
      if (this.currentLanguage === 'unpublished') return article?.lang
      return this.currentLanguage === 'original' ? 'original' : this.currentLanguage
    },
    getSelectedArticleLanguage (article) {
      if (!article) return null
      if (this.currentLanguage === 'unpublished') return article.lang
      return this.currentLanguage
    },
    isUnpublishedArticle (article) {
      const status = article?.status ? article.status.toLowerCase() : ''
      return status === 'translated'
    },
    isUploadableArticle (article) {
      return this.isUnpublishedArticle(article)
    },
    filterUnpublishedArticles (groupedArticles) {
      if (!groupedArticles) return groupedArticles

      return Object.entries(groupedArticles).reduce((acc, [topicName, articles]) => {
        const unpublishedArticles = articles.filter(article => this.isUnpublishedArticle(article))
        if (unpublishedArticles.length > 0) acc[topicName] = unpublishedArticles
        return acc
      }, {})
    },
    getArticleSelectionKey (article) {
      if (!article?.article_id) return null
      return article.lang ? `${article.article_id}-${article.lang}` : `${article.article_id}`
    },
    getUploadTargets () {
      if (this.currentLanguage === 'unpublished') {
        const seen = new Set()
        return this.selectedArticles.reduce((targets, article) => {
          const selectionKey = this.getArticleSelectionKey(article)
          if (!selectionKey || !article.lang || seen.has(selectionKey) || !this.isUploadableArticle(article)) return targets
          seen.add(selectionKey)
          targets.push(this.createUploadTarget(article, article.lang))
          return targets
        }, [])
      }

      if (!this.currentLanguage || this.currentLanguage === 'original') return []

      const articleIds = [...new Set(this.selectedArticles.filter(article => this.isUploadableArticle(article)).map(article => article.article_id).filter(Boolean))]
      return articleIds.map(articleId => {
        const article = this.selectedArticles.find(selectedArticle => selectedArticle.article_id === articleId)
        return this.createUploadTarget(article || { article_id: articleId }, this.currentLanguage)
      })
    },
    async editArticle (article) {
      this.selectedArticle = article // Set the selected article
      console.log('Selected article:', article)
      this.selectedArticleContent = article.final // Set the selected article content
      this.originalArticle = null
      this.originalArticleContent = ''
      await this.loadOriginalArticleForSelected(article)
    },
    copyArticleMarkdown (article) {
      if (!article) return

      const markdown = article.final
      // Add the markdown to the clipboard
      navigator.clipboard.writeText(markdown)
    },
    copySlug (article) {
      if (!article) return

      const slug = article.slug
      // Add the slug to the clipboard
      navigator.clipboard.writeText(slug)
    },
    updateFinalDraft (newContent) {
      if (this.selectedArticle) {
        this.selectedArticle.final = newContent // Update the final draft content
        this.selectedArticleContent = newContent // Update the selected article content
      }
    },
    // Legacy method maintained for compatibility but improved
    async saveContent (articleId) {
      if (!articleId) return

      // Use the new autosave system for better reliability
      await this.performAutosave()
    },
    async closeEditor () {
      // Save any pending changes before closing
      if (this.selectedArticleContent !== this.autosave.lastSavedContent) {
        await this.performAutosave()
      }

      this.selectedArticle = null // Close the editor by resetting the selected article
      this.selectedArticleContent = null // Reset the selected article content
      this.originalArticle = null
      this.originalArticleContent = ''
      this.showCopilot = false // Reset copilot state
      await this.loadData() // Reload
    },
    toggleCopilot () {
      this.showCopilot = !this.showCopilot
    },
    getStatusColor (status) {
      const statusObj = this.statuses.find(s => s.value === status)
      return statusObj ? statusObj.color : null
    },
    getFunnelColor (funnel) {
      const funnelObj = this.funnel.find(f => f.value === funnel)
      return funnelObj ? funnelObj.color : null
    },
    getTypeColor (type) {
      const typeObj = this.types.find(t => t.value === type)
      return typeObj ? typeObj.color : null
    },
    handleEscKey (event) {
      if (event.key === 'Escape') {
        this.closeEditor()
      }
    },
    toggleSelectArticle (article) {
      const selectionKey = this.getArticleSelectionKey(article)
      const index = this.selectedArticles.findIndex(a => this.getArticleSelectionKey(a) === selectionKey)
      if (index === -1) this.selectedArticles.push(article)
      else this.selectedArticles.splice(index, 1)
    },
    isArticleSelected (article) {
      const selectionKey = this.getArticleSelectionKey(article)
      return this.selectedArticles.some(a => this.getArticleSelectionKey(a) === selectionKey)
    },
    clearSelectedArticles () {
      this.selectedArticles = []
    },
    toggleSelectAllInTopic (groupedArticles) {
      // Check if all articles in this topic are already selected
      const allSelected = this.areAllTopicArticlesSelected(groupedArticles)

      if (allSelected) {
        // If all are selected, deselect all in this topic
        this.selectedArticles = this.selectedArticles.filter(selected => {
          return !groupedArticles.some(article => this.getArticleSelectionKey(article) === this.getArticleSelectionKey(selected))
        })
      } else {
        // If not all selected, select all articles in this topic that aren't already selected
        groupedArticles.forEach(article => {
          if (!this.isArticleSelected(article)) {
            this.selectedArticles.push(article)
          }
        })
      }
    },
    areAllTopicArticlesSelected (groupedArticles) {
      if (!groupedArticles || groupedArticles.length === 0) return false
      return groupedArticles.every(article => this.isArticleSelected(article))
    },
    getPublishTo () {
      return this.activeContentplan?.main_cms_integration || 'wordpress'
    },
    getIntegrationLabel (publishTo) {
      if (publishTo === 'magento') return 'Magento'
      if (publishTo === 'custom-cms') return 'Custom CMS'
      if (publishTo === 'shopify') return 'Shopify'
      if (publishTo === 'webflow') return 'Webflow'
      return 'WordPress'
    },
    async waitForUploadLimit () {
      await new Promise(resolve => setTimeout(resolve, this.translationUploadDelay))
    },
    createUploadTarget (article, language) {
      const articleId = article?.article_id || article?.articleId
      return {
        key: articleId && language ? `${articleId}-${language}` : null,
        articleId,
        language,
        title: article?.title || `Article ${articleId}`
      }
    },
    openUploadProgressDialog (title, uploadTargets = []) {
      this.uploadProgressDialog = {
        open: true,
        title,
        items: []
      }
      this.appendUploadProgressTargets(uploadTargets)
    },
    closeUploadProgressDialog () {
      if (this.isUploadProgressRunning) return
      this.uploadProgressDialog.open = false
    },
    appendUploadProgressTargets (uploadTargets) {
      const existingKeys = new Set(this.uploadProgressDialog.items.map(item => item.key))
      const newItems = uploadTargets
        .filter(target => target.key && !existingKeys.has(target.key))
        .map(target => ({
          key: target.key,
          articleId: target.articleId,
          language: target.language,
          title: target.title,
          status: 'queued',
          result: '',
          errorReason: ''
        }))
      this.uploadProgressDialog.items.push(...newItems)
    },
    updateUploadProgressTarget (target, data) {
      const index = this.uploadProgressDialog.items.findIndex(item => item.key === target.key)
      if (index === -1) return
      this.$set(this.uploadProgressDialog.items, index, {
        ...this.uploadProgressDialog.items[index],
        ...data
      })
    },
    getUploadErrorReason (error) {
      const responseData = error?.response?.data
      if (typeof responseData === 'string') return responseData
      return responseData?.message || responseData?.error || error?.message || 'Upload failed'
    },
    getUploadProgressStatusColor (status) {
      if (status === 'uploaded') return 'success'
      if (status === 'uploading') return 'primary'
      return 'grey'
    },
    getUploadProgressResultColor (result) {
      if (result === 'success') return 'success'
      if (result === 'error') return 'error'
      return 'grey'
    },
    async uploadTranslatedTargets (uploadTargets, publishTo) {
      let uploadedCount = 0
      const failedTargets = []

      for (let i = 0; i < uploadTargets.length; i++) {
        const target = uploadTargets[i]
        this.updateUploadProgressTarget(target, {
          status: 'uploading',
          result: '',
          errorReason: ''
        })

        try {
          await this.uploadTranslatedArticle({
            contentplanId: this.contentplanId,
            articleId: target.articleId,
            language: target.language,
            publishTo
          })
          uploadedCount++
          this.updateUploadProgressTarget(target, {
            status: 'uploaded',
            result: 'success',
            errorReason: ''
          })
        } catch (error) {
          console.error('Error uploading translated article:', { target, error })
          const errorReason = this.getUploadErrorReason(error)
          const failedTarget = { ...target, errorReason }
          failedTargets.push(failedTarget)
          this.updateUploadProgressTarget(target, {
            status: 'uploaded',
            result: 'error',
            errorReason
          })
        }

        if (i < uploadTargets.length - 1) await this.waitForUploadLimit()
      }

      return { uploadedCount, failedTargets }
    },
    getGroupedArticleUploadTargets (groupedArticles) {
      const seen = new Set()

      return Object.values(groupedArticles || {}).flat().reduce((targets, article) => {
        const selectionKey = this.getArticleSelectionKey(article)
        if (!selectionKey || !article.lang || seen.has(selectionKey) || !this.isUploadableArticle(article)) return targets
        seen.add(selectionKey)
        targets.push(this.createUploadTarget(article, article.lang))
        return targets
      }, [])
    },
    async handleUploadAllTranslated () {
      if (this.isUploadAllDisabled) {
        this.setSnackbar({
          open: true,
          text: this.uploadDisabledMessage,
          type: 'error'
        })
        return
      }

      this.isLoading.uploadAll = true

      try {
        const publishTo = this.getPublishTo()
        const integrationLabel = this.getIntegrationLabel(publishTo)
        let failedCount = 0
        let uploadedCount = 0
        let hasMore = true
        this.openUploadProgressDialog(`Uploading all translated articles to ${integrationLabel}`)

        while (hasMore) {
          const response = await this.articleTranslationsGet({
            contentplanId: this.contentplanId,
            status: 'translated',
            limit: this.translationUploadLimit,
            offset: failedCount,
            straightReturn: true
          })
          const uploadTargets = this.getGroupedArticleUploadTargets(response?.articles)
          this.appendUploadProgressTargets(uploadTargets)

          if (uploadTargets.length === 0) {
            hasMore = false
          } else {
            const result = await this.uploadTranslatedTargets(uploadTargets, publishTo)
            uploadedCount += result.uploadedCount
            failedCount += result.failedTargets.length
            hasMore = response?.meta?.hasMore || result.uploadedCount > 0
          }
        }

        await this.loadData()

        this.setSnackbar({
          open: true,
          text: failedCount
            ? `Uploaded ${uploadedCount} translation(s) to ${integrationLabel}. ${failedCount} failed.`
            : `Uploaded ${uploadedCount} translation(s) to ${integrationLabel}`,
          type: failedCount ? 'error' : 'success'
        })
      } catch (error) {
        console.error('Error uploading all translated articles:', error)
        this.setSnackbar({
          open: true,
          text: 'Failed to upload translated articles. Please try again.',
          type: 'error'
        })
      } finally {
        this.isLoading.uploadAll = false
      }
    },
    async handleUploadSelected () {
      if (!this.currentLanguage || this.currentLanguage === 'original') {
        this.setSnackbar({
          open: true,
          text: 'Select a translation language before uploading.',
          type: 'error'
        })
        return
      }

      const uploadTargets = this.getUploadTargets()
      if (uploadTargets.length === 0) {
        this.setSnackbar({
          open: true,
          text: 'Select translated articles to upload.',
          type: 'error'
        })
        return
      }

      if (this.isUploadDisabled) {
        this.setSnackbar({
          open: true,
          text: this.uploadDisabledMessage,
          type: 'error'
        })
        return
      }

      this.isLoading.upload = true

      try {
        const publishTo = this.getPublishTo()
        const integrationLabel = this.getIntegrationLabel(publishTo)
        this.openUploadProgressDialog(`Uploading selected translations to ${integrationLabel}`, uploadTargets)
        const { uploadedCount, failedTargets } = await this.uploadTranslatedTargets(uploadTargets, publishTo)

        this.clearSelectedArticles()

        // Reload data to show updated statuses and timestamps
        await this.loadData()

        this.setSnackbar({
          open: true,
          text: failedTargets.length
            ? `Uploaded ${uploadedCount} translation(s) to ${integrationLabel}. ${failedTargets.length} failed.`
            : uploadTargets.length > 1
              ? `Translations uploaded to ${integrationLabel} successfully`
              : `Translation uploaded to ${integrationLabel} successfully`,
          type: failedTargets.length ? 'error' : 'success'
        })
      } catch (error) {
        console.error('Error uploading translated articles:', error)
        this.setSnackbar({
          open: true,
          text: 'Failed to upload translated articles. Please try again.',
          type: 'error'
        })
      } finally {
        this.isLoading.upload = false
      }
    },
    async handleUploadSelectedArticle () {
      if (!this.selectedArticle || this.isSelectedArticleUploadDisabled) {
        this.setSnackbar({
          open: true,
          text: this.uploadDisabledMessage,
          type: 'error'
        })
        return
      }

      if (this.selectedArticleContent !== this.autosave.lastSavedContent) await this.performAutosave()

      this.isLoading.upload = true

      try {
        const publishTo = this.getPublishTo()
        const integrationLabel = this.getIntegrationLabel(publishTo)
        const uploadTargets = [this.createUploadTarget(this.selectedArticle, this.selectedArticleLanguage)]
        this.openUploadProgressDialog(`Uploading translation to ${integrationLabel}`, uploadTargets)
        const { uploadedCount, failedTargets } = await this.uploadTranslatedTargets(uploadTargets, publishTo)

        if (!failedTargets.length && uploadedCount > 0) this.selectedArticle.status = 'published'
        await this.loadData()

        this.setSnackbar({
          open: true,
          text: failedTargets.length
            ? `Failed to upload translation to ${integrationLabel}`
            : `Translation uploaded to ${integrationLabel} successfully`,
          type: failedTargets.length ? 'error' : 'success'
        })
      } catch (error) {
        console.error('Error uploading translated article:', error)
        this.setSnackbar({
          open: true,
          text: 'Failed to upload translated article. Please try again.',
          type: 'error'
        })
      } finally {
        this.isLoading.upload = false
      }
    },
    findArticleInGroupedArticles (groupedArticles, articleId) {
      return Object.values(groupedArticles || {})
        .flat()
        .find(article => article.article_id === articleId) || null
    },
    async loadOriginalArticleForSelected (article) {
      if (!article?.article_id || this.getSelectedArticleLanguage(article) === 'original') {
        this.originalArticle = article || null
        this.originalArticleContent = article?.final || ''
        return
      }

      this.isLoading.originalArticle = true

      try {
        let offset = 0
        let hasMore = true

        while (hasMore && !this.originalArticle) {
          const response = await this.articleTranslationsGet({
            contentplanId: this.contentplanId,
            language: 'original',
            limit: this.translationLoadLimit,
            offset,
            straightReturn: true
          })

          const originalArticle = this.findArticleInGroupedArticles(response?.articles, article.article_id)
          if (originalArticle) {
            this.originalArticle = originalArticle
            this.originalArticleContent = originalArticle.final || ''
            return
          }

          hasMore = Boolean(response?.meta?.hasMore)
          offset = response?.meta?.nextOffset || offset + this.translationLoadLimit
        }
      } catch (error) {
        console.error('Error loading original article:', error)
        this.setSnackbar({
          open: true,
          text: 'Failed to load original article.',
          type: 'error'
        })
      } finally {
        this.isLoading.originalArticle = false
      }
    },
    async handleCopySelected () {
      if (this.selectedArticles.length === 0) return

      const allTitles = this.selectedArticles.map(article => article.title).join('\n')
      await navigator.clipboard.writeText(allTitles)

      this.setSnackbar({
        open: true,
        text: `Copied ${this.selectedArticles.length} article title(s) to clipboard`,
        type: 'success'
      })

      this.clearSelectedArticles()
    },
    handleSnackbarAction (event) {
      console.log('Snackbar action:', event)
      if (event === 'upload-selected') {
        this.handleUploadSelected()
      } else if (event === 'copy-selected') {
        this.handleCopySelected()
      }
    },

    async startTranslation () {
      if (!this.availableLanguages || this.availableLanguages.length === 0) {
        this.setSnackbar({
          open: true,
          text: 'No translation languages configured. Please set up translation settings first.',
          type: 'error'
        })
        return
      }

      this.isLoading.translation = true

      try {
        // Add progress tracker with single step
        this.addProgressTracker({
          contentplanId: this.contentplanId,
          name: 'Translation',
          stepsObject: {
            Translating: 'inProgress'
          }
        })

        await this.sendWebsocketMessage({
          action: 'runTranslateContent',
          message: {
            contentplanId: this.contentplanId
          }
        })

        this.setSnackbar({
          open: true,
          text: 'Translation process started. You will receive updates as it progresses.',
          type: 'success'
        })
      } catch (error) {
        console.error('Error starting translation:', error)
        this.setSnackbar({
          open: true,
          text: 'Failed to start translation process. Please try again.',
          type: 'error'
        })
      } finally {
        this.isLoading.translation = false
      }
    }
  },
  async mounted () {
    await this.loadData()
  },
  beforeDestroy () {
    // Clean up autosave on component destruction
    this.clearAutosave()
  },
  components: {
    MarkdownEditor,
    SnackbarAction,
    ChatGPT
  }
}
